import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Zap, X, Send, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAICopilotContext } from '../context/AICopilotContext';

import { cn } from '../lib/utils';
import { API_BASE_URL } from '../config/api';
import { authorizedFetch } from '../services/api';

type Message = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    isTyping?: boolean;
    isAction?: boolean;
};

export function AICopilot() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            role: 'assistant',
            content: 'Hi! I am the Hadaf ERP Copilot. I can help you navigate around the application. Where would you like to go?',
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { screenContext } = useAICopilotContext();



    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if (!input.trim() || isLoading) return;

        const userMsg = input.trim();
        setInput('');

        const newMessages: Message[] = [
            ...messages,
            { id: Date.now().toString(), role: 'user', content: userMsg }
        ];

        setMessages(newMessages);
        setIsLoading(true);

        try {
            const response = await authorizedFetch(`${API_BASE_URL}/copilot/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    messages: newMessages.map(m => ({
                        role: m.role,
                        content: m.content
                    })),
                    current_route: location.pathname + location.search,
                    screen_context: screenContext
                })
            });

            if (!response.ok) throw new Error('API Error');

            const data = await response.json();

            if (data.type === 'navigation') {
                setMessages(prev => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        role: 'assistant',
                        content: data.message || `Navigating you to ${data.route}...`,
                        isAction: true
                    }
                ]);

                // Allow a tiny delay so the user sees the message before teleporting
                setTimeout(() => {
                    navigate(data.route);
                    setIsOpen(false);
                }, 1500);
            } else {
                // Auto-retry once if response is blank/null
                if (!data.message || data.message.trim() === '') {
                    console.warn('Copilot returned blank response, retrying...');
                    const retryResponse = await authorizedFetch(`${API_BASE_URL}/copilot/chat`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
                            current_route: location.pathname + location.search,
                            screen_context: screenContext
                        })
                    });
                    if (retryResponse.ok) {
                        const retryData = await retryResponse.json();
                        if (retryData.message && retryData.message.trim() !== '') {
                            setMessages(prev => [
                                ...prev,
                                { id: Date.now().toString(), role: 'assistant', content: retryData.message }
                            ]);
                            setIsLoading(false);
                            return;
                        }
                    }
                    // Both attempts returned blank
                    setMessages(prev => [
                        ...prev,
                        { id: Date.now().toString(), role: 'assistant', content: 'Sorry, I took too long to process that. Could you try asking again?' }
                    ]);
                } else {
                    setMessages(prev => [
                        ...prev,
                        { id: Date.now().toString(), role: 'assistant', content: data.message }
                    ]);
                }
            }
        } catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [
                ...prev,
                { id: Date.now().toString(), role: 'assistant', content: 'Sorry, I hit a snag while trying to process that.' }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Chat Window */}
            {isOpen && (
                <div
                    style={{ position: 'fixed', bottom: 24, right: 24, width: 384, height: 500, zIndex: 9999 }}
                >
                    <div className="w-full h-full bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center justify-between text-white shrink-0">
                            <div className="flex items-center gap-2">
                                <div className="bg-white/20 p-1.5 rounded-lg">
                                    <Zap size={18} className="text-white fill-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-sm">Hadaf ERP Copilot</h3>
                                    <p className="text-[10px] text-blue-100 opacity-90">AI Navigation Assistant</p>
                                </div>
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="text-white/80 hover:text-white hover:bg-white/10 p-1 rounded transition-colors cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={cn(
                                        "flex w-full",
                                        msg.role === 'user' ? "justify-end" : "justify-start"
                                    )}
                                >
                                    <div className={cn(
                                        "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm",
                                        msg.role === 'user'
                                            ? "bg-blue-600 text-white rounded-tr-sm"
                                            : msg.isAction
                                                ? "bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-tl-sm font-medium flex items-center gap-2"
                                                : "bg-white text-slate-700 border border-slate-200 rounded-tl-sm"
                                    )}>
                                        {msg.isAction && <Zap size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />}
                                        <div className="leading-relaxed prose prose-sm prose-slate max-w-none">
                                            <ReactMarkdown
                                                components={{
                                                    p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                                    ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                                                    ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                                                    li: ({ node, ...props }) => <li className="marker:text-slate-400" {...props} />,
                                                    strong: ({ node, ...props }) => <strong className="font-semibold text-slate-800" {...props} />,
                                                }}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className="flex w-full justify-start">
                                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin text-blue-500" />
                                        <span className="text-xs text-slate-500 font-medium tracking-wide">Thinking...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="p-3 bg-white border-t border-slate-100 shrink-0">
                            <form
                                onSubmit={handleSubmit}
                                className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pr-1.5 pl-4 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all"
                            >
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Where to?"
                                    className="flex-1 bg-transparent border-none outline-none py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                                    disabled={isLoading}
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    disabled={!input.trim() || isLoading}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white p-2 rounded-full transition-colors flex shrink-0 items-center justify-center h-8 w-8"
                                >
                                    <Send size={14} className="ml-0.5" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Trigger Button */}
            {!isOpen && (
                <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="group flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl shadow-blue-600/30 hover:bg-blue-700 hover:scale-105 transition-all duration-300 ring-4 ring-white"
                    >
                        <MessageSquare size={24} className="group-hover:hidden animate-in fade-in" />
                        <Zap size={24} className="hidden group-hover:block text-white fill-white/20 animate-in fade-in zoom-in" />
                    </button>
                </div>
            )}
        </>
    );
}
